import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { CATEGORIES, type Category } from '@map/shared';
import { categoryStyle, scoreLabel, scoreTone } from '../map/markers.js';
import { useStore } from '../store/StoreContext.js';
import { memberById, poolPlaces, voteScore } from '../store/selectors.js';

export interface PoolPanelProps {
  readonly selectedId: number | null;
  readonly onSelect: (placeId: number) => void;
}

type Sort = 'score' | 'recent' | 'title';

export function PoolPanel({ selectedId, onSelect }: PoolPanelProps): ReactNode {
  const { state, promote } = useStore();
  const [category, setCategory] = useState<Category | 'toutes'>('toutes');
  const [authorId, setAuthorId] = useState<number | 'tous'>('tous');
  const [sort, setSort] = useState<Sort>('score');

  const pool = poolPlaces(state);

  const rows = useMemo(() => {
    const filtered = poolPlaces(state).filter((place) => {
      if (category !== 'toutes' && place.category !== category) return false;
      if (authorId !== 'tous' && place.authorId !== authorId) return false;
      return true;
    });
    const scored = filtered.map((place) => ({ place, score: voteScore(state, place.id) }));
    return scored.sort((a, b) => {
      if (sort === 'score') {
        // A score egal, le plus recent d'abord : c'est ce qui vient d'etre
        // propose qui a besoin d'etre vu et vote.
        if (b.score !== a.score) return b.score - a.score;
        return b.place.createdAt - a.place.createdAt;
      }
      if (sort === 'recent') return b.place.createdAt - a.place.createdAt;
      return a.place.title.localeCompare(b.place.title, 'fr');
    });
  }, [state, category, authorId, sort]);

  return (
    <section className="panel">
      <header className="panel__head">
        <h2 className="panel__title">Vivier</h2>
        <span className="panel__count">{rows.length}</span>
      </header>

      <div className="filters">
        <label className="filters__item">
          <span className="filters__label">Categorie</span>
          <select
            className="field__input field__input--small"
            value={category}
            onChange={(selectEvent) =>
              setCategory(selectEvent.currentTarget.value as Category | 'toutes')
            }
          >
            <option value="toutes">Toutes</option>
            {CATEGORIES.map((entry) => (
              <option key={entry} value={entry}>
                {categoryStyle(entry).label}
              </option>
            ))}
          </select>
        </label>

        <label className="filters__item">
          <span className="filters__label">Auteur</span>
          <select
            className="field__input field__input--small"
            value={String(authorId)}
            onChange={(selectEvent) => {
              const raw = selectEvent.currentTarget.value;
              setAuthorId(raw === 'tous' ? 'tous' : Number(raw));
            }}
          >
            <option value="tous">Tous</option>
            {state.members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.pseudo}
              </option>
            ))}
          </select>
        </label>

        <label className="filters__item">
          <span className="filters__label">Tri</span>
          <select
            className="field__input field__input--small"
            value={sort}
            onChange={(selectEvent) => setSort(selectEvent.currentTarget.value as Sort)}
          >
            <option value="score">Score</option>
            <option value="recent">Recent</option>
            <option value="title">Titre</option>
          </select>
        </label>
      </div>

      <ul className="panel__body cards">
        {rows.map(({ place, score }) => (
          <li
            key={place.id}
            className={`placecard${selectedId === place.id ? ' is-current' : ''}`}
            style={{ '--card-color': categoryStyle(place.category).color } as CSSProperties}
            onClick={() => onSelect(place.id)}
          >
            <span
              className="placecard__chip"
              title={categoryStyle(place.category).label}
              aria-label={categoryStyle(place.category).label}
            >
              {categoryStyle(place.category).glyph}
            </span>

            <span className="placecard__text">
              <span className="placecard__title">{place.title}</span>
              <span className="placecard__meta">
                {categoryStyle(place.category).label}
                <i aria-hidden="true">·</i>
                {memberById(state, place.authorId)?.pseudo ?? 'quelqu un'}
              </span>
            </span>

            <span className={`placecard__score placecard__score--${scoreTone(score)}`}>
              {scoreLabel(score)}
            </span>

            <button
              className="button button--ghost placecard__promote"
              type="button"
              title="Promouvoir dans l itineraire"
              onClick={(clickEvent) => {
                // Sans cette coupure, le clic remonterait a la carte et
                // ouvrirait la fiche en meme temps que la promotion.
                clickEvent.stopPropagation();
                void promote(place.id);
              }}
            >
              Promouvoir
            </button>
          </li>
        ))}

        {rows.length === 0 ? (
          <li className="empty">
            {pool.length === 0 ? (
              <>
                <span className="empty__mark" aria-hidden="true">
                  ◇
                </span>
                <p className="empty__title">Le vivier est vide</p>
                <p className="empty__text">
                  Appuie sur « Ajouter un lieu », puis touche la carte a l endroit qui te fait
                  envie.
                </p>
              </>
            ) : (
              <>
                <span className="empty__mark" aria-hidden="true">
                  ◇
                </span>
                <p className="empty__title">Aucun lieu ne correspond</p>
                <p className="empty__text">Elargis les filtres pour revoir tout le vivier.</p>
              </>
            )}
          </li>
        ) : null}
      </ul>
    </section>
  );
}
